'use client';
import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { CTACard, MarketingVideoPlayer } from '@/components/marketing';
import { CollaborativeCursors } from './CollaborativeCursors';
import { SignUpModal } from '@/components/auth';
import { Camera, Layers, Users } from '@/components/ui/icons';
import { useTranslations as useTranslations$1 } from 'next-intl';
interface FeatureCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    caption?: string;
    children?: React.ReactNode;
}
function FeatureCard({ icon, title, description, caption, children }: FeatureCardProps) {
    return (<div className="group relative flex h-full flex-col">
      { /* Icon */ }
      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/5 text-white/80 transition-colors duration-300 group-hover:bg-white/10 md:h-14 md:w-14">
        {icon}
      </div>

      { /* Title */ }
      <h3 className="mb-3 text-xl font-semibold text-white md:mb-4 md:text-2xl lg:text-2xl">{title}</h3>

      { /* Description */ }
      <p className="mb-6 text-base font-light leading-relaxed text-zinc-300 md:mb-8 md:text-base md:leading-relaxed lg:text-lg lg:leading-relaxed">
        {description}
      </p>

      { /* Visual content */ }
      <div className="mt-auto">
        <div className="group/img relative overflow-hidden rounded-xl md:rounded-2xl">
          <div className="relative rounded-xl bg-gradient-to-br from-zinc-800/60 via-zinc-900/40 to-zinc-800/60 p-[1px] shadow-xl md:rounded-2xl md:shadow-2xl">
            <div className="overflow-hidden rounded-xl bg-zinc-950 shadow-inner md:rounded-2xl">{children}</div>
          </div>
        </div>
      </div>

      { /* Caption */ }
      {caption && <p className="mt-4 text-sm font-medium text-zinc-500 md:mt-5 md:text-sm">{caption}</p>}
    </div>);
}
export function ProblemSection() {
    const t = useTranslations$1();
    const sectionRef = useScrollAnimation<HTMLElement>();
    const hasSignupAccess = useStoredSignupAccess();
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    return (<section ref={sectionRef} id="features" className="animate-on-scroll relative overflow-hidden bg-black px-4 py-12 text-white sm:px-6 md:px-8 md:py-16 lg:px-12 lg:py-24">
      { /* Video Introduction */ }
      <div className="mx-auto mb-10 max-w-4xl md:mb-12 lg:max-w-5xl">
        <MarketingVideoPlayer src="https://reception.martini.film/storage/v1/object/public/public-assets/20250129-v3-webcompress.mp4" className="aspect-video w-full shadow-xl md:rounded-xl" ariaLabel={t("_DEy-n")}/>
      </div>

      <div className="mx-auto max-w-2xl lg:max-w-3xl">
        { /* Section Header */ }
        <div className="mx-auto mb-12 max-w-2xl text-start md:mb-16 md:text-center lg:max-w-3xl">
          <p className="mb-3 text-xs font-medium uppercase tracking-widest text-zinc-500 md:mb-4 md:text-sm">
            {t("p9vSeX")}
          </p>
          <h2 className="mb-4 text-2xl font-bold leading-tight tracking-tight sm:text-3xl md:mb-6 md:text-4xl lg:text-5xl">
            <span className="text-white">{t("BjvoSo")}</span>
            <br/>
            <span className="text-zinc-400">{t("AwKpDE")}</span>
          </h2>
          <p className="text-base font-light leading-relaxed text-zinc-400 md:text-lg md:leading-relaxed lg:text-xl lg:leading-relaxed">
            {t("r32Vld")}
          </p>
        </div>

        { /* Features Stack */ }
        <div className="mx-auto mb-12 max-w-2xl md:mb-16 lg:max-w-3xl">
          <div className="flex flex-col gap-12 md:gap-16 lg:gap-20">
            { /* Feature 1: Camera Control */ }
            <FeatureCard icon={<Camera className="h-6 w-6 md:h-7 md:w-7"/>} title={t("yN4knu")} description={t("dwWi0e")} caption={t("4BrYQi")}>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-cyan-500/10 blur-xl transition-opacity duration-700 group-hover/img:from-purple-500/20 group-hover/img:via-pink-500/20 group-hover/img:to-cyan-500/20"/>
              <div className="relative">
                <video autoPlay loop muted playsInline className="w-full transition-transform duration-500 group-hover/img:scale-[1.02]" aria-label={t("cUFPc1")}>
                  <source src="https://reception.martini.film/storage/v1/object/public/public-assets/3-3d-compmp4.mp4" type="video/mp4"/>
                </video>
              </div>
            </FeatureCard>

            { /* Feature 2: Integrated Pipeline */ }
            <FeatureCard icon={<Layers className="h-6 w-6 md:h-7 md:w-7"/>} title={t("3omJ-l")} description={t("sAqFn3")} caption={t("LSWUzo")}>
              <div className="group/img relative overflow-hidden rounded-lg">
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-teal-500/10 blur-xl transition-opacity duration-700 group-hover/img:from-blue-500/20 group-hover/img:via-cyan-500/20 group-hover/img:to-teal-500/20"/>
                <video autoPlay loop muted playsInline className="relative w-full transition-transform duration-500 group-hover/img:scale-[1.02]" aria-label={t("c3uutl")}>
                  <source src="https://reception.martini.film/storage/v1/object/public/public-assets/1-integrated-comp.mp4" type="video/mp4"/>
                </video>
              </div>
            </FeatureCard>

            { /* Feature 3: Built for Teams */ }
            <FeatureCard icon={<Users className="h-6 w-6 md:h-7 md:w-7"/>} title={t("HRxUEQ")} description={t("RRwRc0")}>
              <div className="group/img relative overflow-hidden rounded-lg">
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-yellow-500/10 blur-xl transition-opacity duration-700 group-hover/img:from-orange-500/20 group-hover/img:via-amber-500/20 group-hover/img:to-yellow-500/20"/>
                <div className="relative">
                  <CollaborativeCursors/>
                  <video autoPlay loop muted playsInline className="w-full transition-transform duration-500 group-hover/img:scale-[1.02]" aria-label={t("mn3ZTl")}>
                    <source src="https://reception.martini.film/storage/v1/object/public/public-assets/2-collab-comp.mp4" type="video/mp4"/>
                  </video>
                </div>
              </div>
            </FeatureCard>
          </div>
        </div>

        { /* CTA Card */ }
        <CTACard className="mx-auto max-w-2xl text-start md:text-center">
          <h3 className="mb-2 text-lg font-semibold text-zinc-200 md:mb-3 md:text-xl">{t("P-1fsm")}</h3>
          <p className="mb-5 text-base font-normal leading-relaxed text-zinc-200 md:text-base lg:text-lg">
            {t("McvLMj")}
          </p>
          {hasSignupAccess ? (<button type="button" onClick={()=>setShowSignUpModal(true)} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]">
              {t("PEa8RR")}
            </button>) : (<a href="https://form.typeform.com/to/VVwyeUrj" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]">
              {t("UnR8YN")}
            </a>)}
        </CTACard>
      </div>

      <SignUpModal isOpen={showSignUpModal} onClose={()=>setShowSignUpModal(false)} returnPath="/projects" defaultMode="signup" variant="marketing"/>
    </section>);
}
